"use client";

import React from "react";
import { ExternalLink } from "lucide-react";

const cormorant = {
    className: "font-cormorant font-serif",
};

export interface DossierItem {
    label: string;
    value: string;
    isLink?: boolean;
    linkUrl?: string;
}

export interface GuestiqoDossierBlockProps {
    dossierTitle?: string;
    dossierItems?: DossierItem[];
    summaryTag?: string;
    tagline?: string;
    paragraphs?: string[];
}

export default function GuestiqoDossierBlock({
    dossierTitle = "Project Dossier",
    dossierItems = [
        { label: "Product Type", value: "SaaS Platform — Multi-Tenant Guidebook" },
        { label: "Industry", value: "Hospitality / Travel-Tech" },
        { label: "Scope", value: "Brand Strategy, UX/UI Core, React Engineering, DevOps Infrastructure" },
        { label: "Timeline", value: "8 Months — Strategy to Live Launch" },
        { label: "Live Endpoint", value: "guestiqo.uk", isLink: true, linkUrl: "https://guestiqo.uk" }
    ],
    summaryTag = "( Executive Summary )",
    tagline = "Replacing paper binders, static emails, and messy messaging with a single tactile interface.",
    paragraphs = [
        "Guestiqo was born from a simple hypothesis: that the UK short-term rental market — worth over £4.2 billion — remains heavily reliant on outdated printed binders, cluttered WhatsApp messages, and copy-pasted booking templates to explain basic stay instructions. We set out to streamline all guest operations into an elegant mobile guidebook.",
        "Kitebe partnered with Guestiqo as their product design and software engineering studio. We designed the complete visual brand identity, built the high-performance multi-tenant web application architecture, developed custom QR-code provisioning pipelines, and established the secure cloud backend capable of scaling dynamically across thousands of properties.",
        "Today, Guestiqo serves boutique cottages, countryside estates, and Airbnb hosts across England, Wales, and Scotland, transforming how guests experience their stays and easing communication loads for property managers."
    ]
}: GuestiqoDossierBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#FAF7F2] border-t border-b border-[#1C1C1C]/5 w-full" data-header-theme="dark">
            <div className="container mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
                    {/* Metadata Dossier */}
                    <div className="lg:col-span-4 bg-[#F7F3EB] border border-[#1C1C1C]/10 p-8 rounded-2xl shadow-xs">
                        <h3 className={`${cormorant.className} text-2xl font-medium mb-6 text-[#1C1C1C]`}>
                            {dossierTitle}
                        </h3>
                        <div className="space-y-5 text-sm font-sans">
                            {dossierItems.map((item, idx) => (
                                <div key={idx} className={idx > 0 ? "border-t border-[#1C1C1C]/5 pt-3" : ""}>
                                    <span className="text-[10px] uppercase font-mono text-[#8C8276] tracking-widest block mb-1">
                                        {item.label}
                                    </span>
                                    {item.isLink && item.linkUrl ? (
                                        <a href={item.linkUrl} target="_blank" rel="noopener noreferrer" className="text-[#1C1C1C] font-semibold underline hover:text-[#4E4E4E] inline-flex items-center gap-1">
                                            {item.value} <ExternalLink className="w-3.5 h-3.5" />
                                        </a>
                                    ) : (
                                        <span className="text-[#1C1C1C] font-medium">{item.value}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Project Narrative */}
                    <div className="lg:col-span-8 space-y-6">
                        {summaryTag && (
                            <span className="text-xs font-mono uppercase tracking-widest text-[#8C8276] block mb-2">
                                {summaryTag}
                            </span>
                        )}
                        {tagline && (
                            <h2 className={`${cormorant.className} text-3xl md:text-5xl font-light text-[#1C1C1C] leading-tight`}>
                                {tagline}
                            </h2>
                        )}
                        <div className="space-y-6 text-base text-[#4E4E4E] leading-relaxed font-light max-w-3xl pt-4 font-sans">
                            {paragraphs.map((p, idx) => (
                                <p key={idx}>{p}</p>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
